import { Inject, Injectable, InjectionToken } from '@angular/core';
import { PaginatedState, type PaginatedStateConfig, PaginatedStateModel } from '@shared/ui';

export interface AssetTypeData {
  id: string;
  name: string;
  type: 'image' | 'video' | 'text' | 'audio';
  deviceTypeIds: string[];
  supportedDevices: number;
  usedByCaseTypes?: number; // undefined = not loaded yet (deferred loading)
  usedByMethodTypes?: number; // undefined = not loaded yet (deferred loading)
}

/**
 * Full asset type data used for editing, includes device type IDs
 */
export interface AssetTypeEditorData {
  id?: string;
  name: string;
  description?: string;
  type: 'image' | 'video' | 'text' | 'audio';
  version?: string;
  deviceTypeIds: string[];
}

export interface AssetTypeStateModel extends PaginatedStateModel<AssetTypeData> {
  // Editor state
  selectedAssetType?: AssetTypeEditorData;
  saving: boolean;
  saved: boolean;
}

export const ASSET_TYPE_STATE_CONFIG = new InjectionToken<Partial<PaginatedStateConfig>>('ASSET_TYPE_STATE_CONFIG');

@Injectable()
export class AssetTypeState extends PaginatedState<AssetTypeData, AssetTypeStateModel> {
  constructor(@Inject(ASSET_TYPE_STATE_CONFIG) config?: Partial<PaginatedStateConfig>) {
    super(config, { selectedAssetType: undefined, saving: false, saved: false });
  }

  // ============================================================================
  // Custom Selectors
  // ============================================================================

  public readonly selectedAssetType$ = this.select((state) => state.selectedAssetType);
  public readonly saving$ = this.select((state) => state.saving);
  public readonly saved$ = this.select((state) => state.saved);
  public readonly isNewAssetType$ = this.select((state) => !state.selectedAssetType?.id);

  // Backwards-compat aliases
  public readonly assetTypes$ = this.items$;
  public readonly nextAssetTypeToken$ = this.nextToken$;
  public readonly previousAssetTypeTokens$ = this.previousTokens$;
  public readonly assetTypesLoaded$ = this.itemsLoaded$;
  public readonly noAssetTypes$ = this.noItems$;

  // ============================================================================
  // Custom Updaters (public for Effects access)
  // ============================================================================

  /**
   * Initialize the editor with an empty asset type for creation
   */
  public readonly initializeNewAssetType = this.updater((state) => ({
    ...state,
    selectedAssetType: {
      name: '',
      description: '',
      type: 'image' as const,
      version: '',
      deviceTypeIds: [],
    },
    saving: false,
    saved: false,
  }));

  /**
   * Reset the editor state
   */
  public readonly resetEditor = this.updater((state) => ({
    ...state,
    selectedAssetType: undefined,
    saving: false,
    saved: false,
  }));

  public readonly setSaving = this.updater((state, saving: boolean) => ({
    ...state,
    saving,
  }));

  public readonly setSaved = this.updater((state, assetType: AssetTypeEditorData) => ({
    ...state,
    selectedAssetType: assetType,
    saving: false,
    saved: true,
  }));

  public readonly setEditorError = this.updater((state, error: string) => ({
    ...state,
    error,
    saving: false,
  }));

  public readonly setSelectedAssetType = this.updater((state, assetType: AssetTypeEditorData) => ({
    ...state,
    selectedAssetType: assetType,
    loading: false,
    saving: false,
    saved: false,
  }));
}
